import { createSlice } from '@reduxjs/toolkit';

import { setState } from '@app/helpers/store';
import { DASHBOARD_SLICE_PATHS } from './constants';

const emptySlice = () => ({
  isFetching: false,
  // Whether a fetch attempt has finished, regardless of outcome — an errored
  // slice counts as settled, or one failing endpoint would hold the whole tab
  // on its loading state forever.
  hasCompleted: false,
  data: null,
  error: null,
});

const buildInitialState = () =>
  Object.entries(DASHBOARD_SLICE_PATHS).reduce(
    (dashboards, [id, slicePaths]) => ({
      ...dashboards,
      [id]: Object.keys(slicePaths).reduce(
        (slices, name) => ({ ...slices, [name]: emptySlice() }),
        {},
      ),
    }),
    {},
  );

const insightsViewsSlice = createSlice({
  name: 'insightsViews',
  initialState: buildInitialState(),
  reducers: {
    fetchDashboardRequest(state, { payload: { id } }) {
      const dashboard = state[id];

      if (!dashboard) {
        return;
      }

      // `data` is cleared rather than kept: the subsidiary is part of the
      // request, so holding the previous payload while the new one is in flight
      // would briefly render another client's numbers.
      Object.keys(dashboard).forEach(name => {
        setState(dashboard[name], {
          isFetching: true,
          hasCompleted: false,
          data: null,
          error: null,
        });
      });
    },
    fetchSliceSuccess(state, { payload: { id, slice, data } }) {
      if (!state[id]?.[slice]) {
        return;
      }

      setState(state[id][slice], {
        isFetching: false,
        hasCompleted: true,
        data,
        error: null,
      });
    },
    fetchSliceError(state, { payload: { id, slice, error } }) {
      if (!state[id]?.[slice]) {
        return;
      }

      setState(state[id][slice], {
        isFetching: false,
        hasCompleted: true,
        data: null,
        error,
      });
    },
  },
});

const { actions, reducer } = insightsViewsSlice;

export const { fetchDashboardRequest, fetchSliceSuccess, fetchSliceError } =
  actions;

export default reducer;
